import { Image, styled, Text, useTranslation } from '@obvio/app'
import { SvgCalendarMini } from '@obvio/svg'
import { RichText } from '@obvio/template'
import { Grid, Section, Stack } from '@obvio/ui'

import { ReadMoreLink } from './ReadMoreLink'
import { HeroSection } from './Renderer/RendererVariants/HeroSection'
import { customRenderers, getImage } from './Renderer/utils'
import { useProfitRoom } from './useProfitRoom'

import type { Data } from '@/pages/pakiety/[slug]'
import type { ReactElement, ReactNode } from 'react'

type RoomCategoryPageProps = {
  offer: Data
}

export function ProfitroomOfferPage({
  offer,
}: RoomCategoryPageProps): ReactElement {
  const { t } = useTranslation()

  return (
    <>
      <HeroSection
        type="image"
        img={getImage(offer.image)}
        text={offer.title}
        heading
      />
      {customRenderers['text-richText-heading-richText-cta']({
        text: 'PAKIET',
        richTextHeading: `<F1>${offer.title.toUpperCase()}<F1>`,
        richText: offer.description,
      })}
      <Section>
        <TwoImages {...offer} />
        <PackageInfo {...offer} />
        <Texts {...offer} />
      </Section>
      {customRenderers['title-richText']({
        title: t('text.seeAlso'),
        richText: `<F1>${t('text.otherOffers')}<F1>\n<F2>${t(
          'text.chooseAndBook',
        )}<F2>`,
      })}
      {customRenderers['packages-carousel']({})}
    </>
  )
}

const StyledInfo = styled(Stack)`
  padding-top: 3rem;
  justify-content: space-between;
  ${styled.notDesktop`
    flex-direction: column;
    row-gap: 1.5rem;
  `}
`

const Separator = styled.div`
  border-bottom: 1px solid ${(theme) => theme.stroke};
  margin: 1.5rem 0;
  a {
    margin: 0 !important;
  }
`

const StyledInfoItems = styled.div`
  display: flex;
  gap: 1.5rem;
  ${styled.mobile`
    flex-direction: column;
    gap: 0;
  `}
`

function PackageInfo({
  title,
  availableFrom,
  availableTo,
  startingPrice,
  pricePer,
  profitroomId,
}: Data): ReactElement {
  const { t, locale } = useTranslation()
  const { bookingOpen } = useProfitRoom()

  const availability = []

  if (availableFrom && availableFrom > new Date()) {
    availability.push(
      t('label.from', { date: availableFrom.toLocaleDateString(locale) }),
    )
  }

  if (availableTo) {
    availability.push(
      t('label.to', { date: availableTo.toLocaleDateString(locale) }),
    )
  }

  if (availability.length > 0) {
    availability.unshift(t('text.valid'))
  }

  const dates = availability.join(' ')
  const price = `${t('text.priceFrom', { price: startingPrice })} ${pricePer}`

  return (
    <>
      <StyledInfo>
        <div>
          <Text tag="h3">Pakiet “{title}”</Text>
          <StyledInfoItems>
            {dates ? <InfoItem svg={<SvgCalendarMini />} text={dates} /> : null}
            <InfoItem text={price} />
          </StyledInfoItems>
        </div>
        <ReadMoreLink onClick={() => bookingOpen?.({ OfferId: profitroomId })}>
          {t('label.booking')}
        </ReadMoreLink>
      </StyledInfo>
      <Separator />
    </>
  )
}

const StyledInfoItem = styled.div`
  display: flex;
  align-items: center;
  padding-top: 1rem;
  gap: 0.5rem;
`

function InfoItem({
  svg,
  text,
}: {
  svg?: ReactNode
  text: string
}): ReactElement {
  return (
    <StyledInfoItem>
      {svg}
      <Text>{text}</Text>
    </StyledInfoItem>
  )
}

const StyledGrid = styled(Grid)`
  grid-template-columns: 1fr 0.65fr;
  gap: 2.25rem;
`

const StyledImages = styled(StyledGrid)`
  aspect-ratio: 2.3;
`

function TwoImages({
  image1,
  image2,
}: Pick<Data, 'image1' | 'image2'>): ReactElement {
  return (
    <StyledImages>
      <Image img={getImage(image1)} />
      <Image img={getImage(image2)} />
    </StyledImages>
  )
}

const StyledTexts = styled(StyledGrid)`
  ${styled.mobile`
    grid-template-columns: 1fr;
  `}
`

function Texts({ text1, text2 }: Pick<Data, 'text1' | 'text2'>): ReactElement {
  return (
    <StyledTexts>
      <RichText value={text1} />
      <RichText value={text2} />
    </StyledTexts>
  )
}
